import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles, Checkbox,
    TextField, Button, fade,
    useMediaQuery,
} from '@material-ui/core';
import {
    useQuery, useLazyQuery, useMutation,
} from '@apollo/client';
import { useTheme } from '@material-ui/core/styles';
import { FetchPolicy } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import ButtonStyles from 'styles/theme/Button';
import InputNumber from 'components/widgets/InputNumber';
import VirtualTable from 'components/widgets/VirtualTable';
import InspectionImagesDialog from 'components/modules/inventory/read/InspectionImagesDialog';
import RepairItemPanel from 'components/modules/inventory/read/panels/RepairItemPanel';
import useImagesActions from 'components/modules/inventory/hooks/useImagesActions';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme, fade));
const useStyles = makeStyles((theme) => ({
    main: {
        display: 'flex',
        minWidth: '1020px',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            marginTop: '20px',
            minWidth: 'initial',
        },
    },
    tableContainer: {
        marginTop: '15px',
        paddingLeft: '15px',
        paddingRight: '15px',
        paddingBottom: '134px',
        height: '81vh',
        width: '100%',
        minWidth: '1300px',
        overflow: 'hidden',
        position: 'relative',
    },
    uploadButton: {
        fontSize: '12px',
        borderRadius: '0px',
    },
    greenUploadButton: {
        backgroundColor: `${theme.palette.background.green} !important`,
    },
    textFieldSmall: {
        '& input': {
            padding: 7,
        },
    },
}));

const RejectedListTab = ({ stockNumber, canWrite }) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [state, setState] = useState({
        records: [],
        openImagePreview: false,
        photoIndex: -1,
        isUploadingImages: false,
        isPicturesFormOpen: false,
        selectedItem: null,
        selectedItemImages: null,
    });

    const {
        moveType, setPhotoIndex,
        addImages, deleteImages,
    } = useImagesActions();

    const {
        data,
        loading,
        error,
        refetch,
    } = useQuery(InventoryQuery.GET_REJECTED_ITEMS, {
        variables: {
            stockNumber,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getImages] = useLazyQuery(InventoryQuery.GET_VEHICLE_RECON_ITEM_IMAGES, {
        onCompleted: (response) => {
            if (response) {
                const images = response.getVehicleReconItemImages;
                setState((prevState) => ({
                    ...prevState,
                    selectedItemImages: images,
                }));
            }
        },
        onError: (err) => {
            ModalUtils.errorMessage([err]);
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const [saveComment] = useMutation(InventoryMutation.SAVE_VEHICLE_RECON_ITEM_COMMENT, {
        onError: (err) => {
            ModalUtils.errorMessage(null, err);
        },
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }

        if (!loading && data) {
            setState((prevState) => ({
                ...prevState,
                records: data.getRejectedItems,
            }));
        }
    }, [loading, error, data]);

    const {
        records,
        isPicturesFormOpen,
        selectedItem,
        selectedItemImages,
        openImagePreview,
        photoIndex,
        isUploadingImages,
    } = state;
    const togglePicturesForm = async (vehicleInspectionItemId) => {
        if (vehicleInspectionItemId == null) {
            setState((prevState) => ({
                ...prevState,
                isPicturesFormOpen: false,
                selectedItem: null,
                selectedItemImages: null,
            }));

            refetch();
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isPicturesFormOpen: true,
            selectedItem: vehicleInspectionItemId,
            selectedItemImages: null,
        }));

        getImages({
            variables: {
                vehicleInspectionItemId,
            },
        });
    };

    const toggleImagePreview = (index) => {
        setState((prevState) => ({
            ...prevState,
            openImagePreview: !prevState.openImagePreview,
            photoIndex: index,
        }));
    };

    const onSetPhotoIndex = (type) => setPhotoIndex(
        type,
        photoIndex,
        selectedItemImages,
        setState,
        null,
    );

    const onChange = (vehicleInspectionItemId, field, value) => {
        setState((prevState) => ({
            ...prevState,
            records: records.map((r) => {
                const cloneItem = { ...r };
                if (cloneItem.vehicleInspectionItemId === vehicleInspectionItemId) cloneItem[field] = value;

                return cloneItem;
            }),
        }));
    };

    const onAddImages = async (files) => {
        const dispatcherPayload = {
            ...state,
            isUploadingImages: true,
        };

        const additionalDispatcherPayload = {
            ...state,
            isUploadingImages: false,
        };

        const pathPrefix = selectedItem
            ? `${stockNumber}/inspection/${selectedItem}`
            : `${stockNumber > 0 ? `${stockNumber}` : ''}/inspection`;

        addImages(
            pathPrefix,
            files,
            setState,
            dispatcherPayload,
            additionalDispatcherPayload,
            'vehicleInspectionItemId',
            selectedItem,
            stockNumber,
            InventoryMutation.SAVE_VEHICLE_RECON_ITEM_IMAGES,
            getImages,
        );
    };

    const onDeleteImages = async (selectedImages, toggleImagesDeletionPrompt) => {
        const payload = {
            variables: {
                vehicleInspectionItemId: selectedItem,
            },
        };

        deleteImages(
            selectedItemImages,
            'vehicleInspectionItemImageId',
            InventoryMutation.REMOVE_VEHICLE_RECON_ITEM_IMAGES,
            selectedImages,
            toggleImagesDeletionPrompt,
            getImages,
            payload,
        );
    };

    const onSaveComment = (vehicleInspectionItemId) => {
        const item = records.find((r) => r.vehicleInspectionItemId === vehicleInspectionItemId);
        if (!item) return;

        saveComment({
            variables: {
                vehicleInspectionItemId,
                comment: item.comment,
            },
        });
    };

    const columns = [
        {
            label: '',
            dataKey: 'number',
            width: 80,
            cellRenderer: (cell) => {
                const { rowIndex } = cell;

                return (
                    <span>{rowIndex + 1}</span>
                );
            },
        },
        {
            label: 'Repair Item',
            dataKey: 'repairItem',
            width: 250,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        name,
                    },
                } = cell;

                return (
                    <span>{name}</span>
                );
            },
        },
        {
            label: 'Part',
            dataKey: 'part',
            width: 100,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        part,
                    },
                } = cell;

                return (
                    <Checkbox
                        checked={part}
                    />
                );
            },
        },
        {
            label: 'Comments',
            dataKey: 'comments',
            width: 350,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        comment,
                        vehicleInspectionItemId,
                    },
                } = cell;

                return (
                    <TextField
                        disabled={!canWrite}
                        className={classes.textFieldSmall}
                        value={comment || ''}
                        name="comment"
                        onChange={({ target: { name, value } }) => onChange(vehicleInspectionItemId, name, value)}
                        onBlur={() => onSaveComment(vehicleInspectionItemId)}
                        variant="outlined"
                        size="small"
                    />
                );
            },
        },
        {
            label: 'Estimate',
            dataKey: 'estimate',
            width: 200,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        estimation,
                    },
                } = cell;

                return (
                    <InputNumber
                        disabled
                        value={estimation}
                        placeholder="$0"
                        showCurrency
                        min={0}
                        decimalScale={0}
                        thousandSeparator
                        size="sm"
                    />
                );
            },
        },
        {
            label: 'Pictures',
            dataKey: 'pictures',
            width: 150,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        images,
                        vehicleInspectionItemId,
                    },
                } = cell;
                if (!canWrite) return null;

                return (
                    <Button
                        size="small"
                        className={clsx(
                            classes.containedSecondaryInfo,
                            classes.uploadButton,
                            (images || 0) > 0 ? classes.greenUploadButton : '',
                        )}
                        onClick={() => togglePicturesForm(vehicleInspectionItemId)}
                    >
                        {`View (${images || 0})`}
                    </Button>
                );
            },
        },
    ];

    return (
        <>
            <div className={classes.main}>
                {isMobile && records.map((item) => (
                    <RepairItemPanel
                        index={item.vehicleInspectionItemId}
                        key={`item-${item.vehicleInspectionItemId}`}
                        onChange={onChange}
                        saveComment={onSaveComment}
                        data={item}
                        canWrite={false}
                        togglePicturesForm={togglePicturesForm}
                        isApproved
                        isRejectedItem
                    />
                ))}
                {!isMobile && (
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={loading}
                            rowHeight={35}
                            totalRecords={records.length}
                            data={records}
                            columns={columns}
                        />
                    </div>
                )}
            </div>
            <InspectionImagesDialog
                isPicturesFormOpen={isPicturesFormOpen}
                isUploadingImages={isUploadingImages}
                openImagePreview={openImagePreview}
                addImages={onAddImages}
                canWrite={canWrite}
                togglePicturesForm={togglePicturesForm}
                selectedItemImages={selectedItemImages}
                onOpenImage={toggleImagePreview}
                photoIndex={photoIndex}
                toggleImagePreview={toggleImagePreview}
                setPhotoIndex={onSetPhotoIndex}
                onDeleteImages={onDeleteImages}
                moveType={moveType}
                isApproved={false}
            />
        </>
    );
};

RejectedListTab.propTypes = {
    stockNumber: PropTypes.number.isRequired,
    canWrite: PropTypes.bool.isRequired,
};

export default RejectedListTab;
